import _ from 'lodash';
import { ReportDataProvider } from './ReportDataProvider';
import { tableColumnSettings } from './reportTableColumnSettings';
import {
  GeneralReportTableData,
  Metrics,
  ReportDimension,
  ReportRecord
} from 'core/report/ReportData';
import { divideValue } from './ReportDataHelper';
import { isRoles } from 'core/permission/PermissionDSL';
import { RoleNames, Actor } from 'core';
import { ColumnDefinition } from 'components/TableColumn/TableColumn';

enum COLUMNS {
  NAME = 'name',
  IMPRES = 'impres',
  CLICKS = 'clicks',
  CTR = 'ctr',
  VIEWABLE = 'viewable',
  VCTR = 'vctr',
  CPM = 'cpm',
  CPC = 'cpc',
  CPA = 'cpa',
  CONVS = 'convs',
  CONV_1 = 'conv_1',
  CONV_7 = 'conv_7',
  CVR = 'cvr',
  MEDIA_COST = 'mediaCost',
  MEDIA_SPENT = 'mediaSpent',
  AGENCY_PROFIT = 'agcProfit',
  SYS_PROFIT = 'sysProfit',
  DATA_COST = 'dataCost',
  SPENT = 'spent',
  TAGS = 'tags',
  UU = 'uu',
  EDITBTNS = 'editBtns'
}

export class GeneralReportDataProvider implements ReportDataProvider {
  constructor(
    private onDimensionNameClick: (
      dimension: string,
      rowNameValue: string | number
    ) => void,
    private onDateClick: (date: string) => void
  ) {}

  get avalableDimensions() {
    return [
      ReportDimension.DAY,
      ReportDimension.AGENCY,
      ReportDimension.ADVERTISER,
      ReportDimension.ORDER,
      ReportDimension.L1_OBJECT,
      ReportDimension.L2_OBJECT,
      ReportDimension.CAMPAIGN_TYPE,
      ReportDimension.SALES_CHANNEL,
      ReportDimension.L3_CHANNEL,
      ReportDimension.CREATIVE,
      ReportDimension.CREATIVE_TYPE,
      ReportDimension.CREATIVE_SIZE,
      ReportDimension.SELF_BUY
    ];
  }

  getReportDimensions(actor: Actor | null): ReportDimension[] {
    const isSysAdmin = isRoles(RoleNames.sysAdmin).visible({ actor });
    const isAdvertiser = isRoles(
      RoleNames.adsAdmin,
      RoleNames.adsReport,
      RoleNames.adsSales
    ).visible({ actor });
    const forbidDimensions = isSysAdmin
      ? [
          ReportDimension.MONTH,
          ReportDimension.HOUR,
          ReportDimension.DEVICE_TYPE
        ]
      : [
          ReportDimension.MONTH,
          ReportDimension.HOUR,
          ReportDimension.DEVICE_TYPE,
          ReportDimension.AGENCY
        ];
    isAdvertiser && forbidDimensions.push(ReportDimension.ADVERTISER);
    return this.avalableDimensions.filter(
      (dimension) => forbidDimensions.indexOf(dimension) === -1
    );
  }

  getReportTableColumnSettings(
    allowMetrics: Metrics[],
    tableData: GeneralReportTableData,
    dimension: ReportDimension,
    currency: string
  ): ColumnDefinition[] {
    const allColumnSettings = tableColumnSettings(
      tableData,
      dimension,
      currency,
      this.onDimensionNameClick,
      this.onDateClick
    );
    const tableAllowMetrics = [...allowMetrics, COLUMNS.NAME, COLUMNS.EDITBTNS];
    if (allowMetrics.indexOf(Metrics.SPENT) !== -1) {
      allowMetrics.indexOf(Metrics.IMPRES) !== -1 &&
        tableAllowMetrics.push(COLUMNS.CPM);
      allowMetrics.indexOf(Metrics.CLICKS) !== -1 &&
        tableAllowMetrics.push(COLUMNS.CPC);
      allowMetrics.indexOf(Metrics.CONVS) !== -1 &&
        tableAllowMetrics.push(COLUMNS.CPA);
    }
    if (allowMetrics.indexOf(Metrics.CLICKS) !== -1) {
      allowMetrics.indexOf(Metrics.IMPRES) !== -1 &&
        tableAllowMetrics.push(COLUMNS.CTR);
      allowMetrics.indexOf(Metrics.VIEWABLE) !== -1 &&
        tableAllowMetrics.push(COLUMNS.VCTR);
      allowMetrics.indexOf(Metrics.CONVS) !== -1 &&
        tableAllowMetrics.push(COLUMNS.CVR);
    }
    dimension === ReportDimension.CAMPAIGN &&
      tableAllowMetrics.push(COLUMNS.TAGS);
    return _.compact(
      Object.values(COLUMNS).map((column) => {
        return (
          tableAllowMetrics.indexOf(column) !== -1 && allColumnSettings[column]
        );
      })
    );
  }

  getReportTableData(records: ReportRecord[]): GeneralReportTableData[] {
    return records.map((record) => {
      return {
        key: record.dimensionName + _.get(record, 'dimensionID', 0),
        id: _.get(record, 'dimensionID', 0).toString(),
        tags: record.tags,
        parentId: record.parentId,
        extra: record.extra,
        [COLUMNS.NAME]: record.dimensionName,
        [COLUMNS.IMPRES]: record.impres,
        [COLUMNS.CLICKS]: record.clicks,
        [COLUMNS.CTR]: divideValue(
          _.get(record, 'clicks', 0) * 100,
          _.get(record, 'impres', 0)
        ),
        [COLUMNS.VIEWABLE]: record.viewable,
        [COLUMNS.VCTR]: divideValue(
          _.get(record, 'clicks', 0) * 100,
          _.get(record, 'viewable', 0)
        ),
        [COLUMNS.CPM]: divideValue(
          _.get(record, 'spent', 0) * 1000,
          _.get(record, 'impres', 0)
        ),
        [COLUMNS.CPC]: divideValue(
          _.get(record, 'spent', 0),
          _.get(record, 'clicks', 0)
        ),
        [COLUMNS.CPA]: divideValue(
          _.get(record, 'spent', 0),
          _.get(record, 'convs', 0)
        ),
        [COLUMNS.CONVS]: record.convs,
        [COLUMNS.CONV_1]: record.conv_1,
        [COLUMNS.CONV_7]: record.conv_7,
        [COLUMNS.CVR]: divideValue(
          _.get(record, 'convs', 0) * 100,
          _.get(record, 'clicks', 0)
        ),
        [COLUMNS.MEDIA_COST]: _.get(record, 'mediaCost', 0),
        [COLUMNS.MEDIA_SPENT]: _.get(record, 'mediaSpent', 0),
        [COLUMNS.AGENCY_PROFIT]: _.get(record, 'agcProfit', 0),
        [COLUMNS.SYS_PROFIT]: _.get(record, 'sysProfit', 0),
        [COLUMNS.DATA_COST]: _.get(record, 'dataCost', 0),
        [COLUMNS.SPENT]: _.get(record, 'spent', 0),
        [COLUMNS.UU]: record.uu ? record.uu : 0
      };
    });
  }
}
